import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { BookOpen, X, ArrowUpRight } from "@phosphor-icons/react";

/**
 * Library Cross-Reference — surfaces up to two possible prior precedents from
 * the Firm Library when scoping a new engagement.
 *
 * Presentation rules enforced here (per product brief):
 *   • Labelled "Possible precedents" — never "recommendations".
 *   • Each row explains WHY it was selected in a short structured label.
 *   • Contextual differences (client, industry, geography, engagement type)
 *     are shown so the partner can weigh transferability.
 *   • Dismissible per-engagement — dismissals persist on the server.
 *   • The card shows only partner-visible metadata; opening a memo still
 *     requires normal firm-library permissions on the detail route.
 */
export default function PrecedentsCard({ engagementId }) {
  const [state, setState] = useState({ loading: true, precedents: [], note: null });

  const load = () => {
    setState((s) => ({ ...s, loading: true }));
    api.get(`/engagements/${engagementId}/precedents`)
      .then((r) => setState({ loading: false, precedents: r.data?.precedents || [], note: r.data?.note || null }))
      .catch(() => setState({ loading: false, precedents: [], note: null }));
  };

  useEffect(() => {
    if (engagementId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagementId]);

  const dismiss = async (memoId) => {
    // Iter 67 · Optimistic local removal. Previously we called
    // `load()` after the POST, but the backend always tops the list
    // back up to `limit=2` fresh candidates (minus dismissed) — so
    // dismissing suggestion A would silently slot in suggestion C
    // and the user perceived the click as non-functional. The
    // server-side dismissal is still persisted so A won't return on
    // a later refresh; we just don't force-refill the visible slots
    // on this interaction. If the list becomes empty, the guard at
    // the top of render (`if (!precedents.length && !note) return null`)
    // hides the whole card — no empty shell.
    setState((s) => ({
      ...s,
      precedents: s.precedents.filter((p) => p.id !== memoId),
    }));
    try {
      await api.post(`/engagements/${engagementId}/precedents/${memoId}/dismiss`);
    } catch (e) {
      console.warn("dismiss precedent failed", e);
      // Roll back the optimistic removal by re-fetching truth.
      load();
    }
  };

  if (state.loading) return null; // Silent while loading — this is a supporting panel.
  if (!state.precedents.length && !state.note) return null;

  return (
    <div className="border border-border bg-card p-5 md:col-span-2" data-testid="precedents-card">
      <div className="flex items-center gap-2 mb-1">
        <BookOpen size={14} weight="fill" className="text-primary" />
        <div className="text-[0.625rem] font-mono uppercase tracking-[0.24em] text-eyebrow">Possible precedents</div>
      </div>
      <div className="text-xs italic text-muted-foreground mb-4" style={{ fontFamily: "Georgia, serif" }}>
        From the firm library. Not recommendations — read for texture, then decide what transfers.
      </div>

      {state.note && (
        <div className="text-xs text-muted-foreground italic border-l-2 border-primary/40 pl-3">{state.note}</div>
      )}

      {state.precedents.length > 0 && (
        <div className="space-y-3">
          {state.precedents.map((p) => (
            <div
              key={p.id}
              className="border border-border/60 bg-secondary/20 p-4 hover:border-primary/60 transition-colors"
              data-testid={`precedent-${p.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/library?open=${p.id}`}
                    className="text-sm font-display font-bold hover:text-primary inline-flex items-center gap-1.5"
                    data-testid={`precedent-open-${p.id}`}
                  >
                    {p.engagement_name || "(untitled engagement)"}
                    <ArrowUpRight size={12} />
                  </Link>
                  <div className="mt-0.5 text-[0.6875rem] font-mono uppercase tracking-widest text-muted-foreground">
                    {p.client_name || "—"} · {p.engagement_type || "—"} · filed {p.filed_at ? p.filed_at.slice(0, 10) : "—"}
                  </div>
                  <div className="mt-2 text-xs">
                    <span className="text-eyebrow font-mono uppercase tracking-widest text-[0.625rem] mr-1.5">Why:</span>
                    <span className="text-foreground">{p.why_selected}</span>
                  </div>
                  {p.contextual_differences?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`precedent-diffs-${p.id}`}>
                      {p.contextual_differences.map((d, i) => (
                        <span key={i} className="text-[0.625rem] font-mono uppercase tracking-widest border border-amber-500/40 text-amber-500/80 px-1.5 py-0.5">
                          {d}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={(ev) => { ev.stopPropagation(); dismiss(p.id); }}
                  className="text-muted-foreground hover:text-destructive shrink-0"
                  aria-label="Dismiss this precedent"
                  data-testid={`precedent-dismiss-${p.id}`}
                  title="Not relevant"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
